import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api/client';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/stores/auth';
import { AdminLayout } from '@/components/layouts/admin-layout';
import { AsyncErrorState } from '@/components/ui/async-error-state';
import FilterBar from '@/components/common/filter-bar';
import { PageHeader } from '@/components/page/page-header';
import { PageSection } from '@/components/page/page-section';
import { PageToolbar } from '@/components/page/page-toolbar';
import {
  Plus,
  Play,
  Pause,
  Trash2,
  RefreshCw,
  Webhook,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  Eye
} from 'lucide-react';

// Webhook configuration interface
interface WebhookConfig {
  id: number;
  name: string;
  url: string;
  events: string[];
  status: 'active' | 'inactive';
  secret: string;
  createdAt: string;
  updatedAt: string;
}

// Webhook delivery log interface
interface WebhookDelivery {
  id: number;
  webhookId: number;
  eventType: string;
  statusCode: number;
  success: boolean;
  responseBody: string | null;
  errorMessage: string | null;
  duration: number;
  triggeredAt: string;
}

// Webhook statistics interface
interface WebhookStats {
  totalWebhooks: number;
  activeWebhooks: number;
  inactiveWebhooks: number;
  totalDeliveries: number;
  successRate: number;
  failedDeliveries: number;
}

// API response interfaces
interface WebhooksResponse {
  webhooks: WebhookConfig[];
  total: number;
  page: number;
  pageSize: number;
}

interface WebhookDeliveriesResponse {
  deliveries: WebhookDelivery[];
  total: number;
  page: number;
  pageSize: number;
}

interface CreateWebhookData {
  name: string;
  url: string;
  events: string[];
  secret?: string;
}

interface TestWebhookResponse {
  success: boolean;
  statusCode: number;
  response: string;
  duration: number;
}

interface ActionResponse {
  message: string;
}

// Event types available for webhooks
const EVENT_TYPES = [
  'stream.live',
  'stream.ended',
  'recording.ready',
  'stream.recording',
  'comment.created',
  'user.joined',
  'access.requested'
];

export default function WebhooksPage() {
  const { isInitialized } = useAuthStore();

  // Webhooks state
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
  const [stats, setStats] = useState<WebhookStats | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [deliveriesLoading, setDeliveriesLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [deliveriesError, setDeliveriesError] = useState<string | null>(null);

  // Filter state managed by FilterBar
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});

  // Pagination
  const [page, setPage] = useState(1);
  const [totalWebhooks, setTotalWebhooks] = useState(0);
  const [totalDeliveries, setTotalDeliveries] = useState(0);
  const pageSize = 10;

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookConfig | null>(null);
  const [testingWebhookId, setTestingWebhookId] = useState<number | null>(null);
  const [webhookToDelete, setWebhookToDelete] = useState<WebhookConfig | null>(null);

  // Create form state
  const [newWebhook, setNewWebhook] = useState<CreateWebhookData>({
    name: '',
    url: '',
    events: [],
    secret: ''
  });

  // Test result state
  const [testResult, setTestResult] = useState<TestWebhookResponse | null>(null);

  // Fetch webhooks
  const fetchWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...filters
      });

      const response = await api.get<WebhooksResponse>(`/api/admin/webhooks?${params}`);

      if (response.success && response.data) {
        setWebhooks(response.data.webhooks || []);
        setTotalWebhooks(response.data.total || 0);
      } else {
        setError(response.error?.message || 'Failed to load webhooks');
      }
    } catch (err) {
      console.error('Error fetching webhooks:', err);
      setError('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  // Fetch webhook statistics
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      const response = await api.get<WebhookStats>('/api/admin/webhooks/stats');

      if (response.success && response.data) {
        setStats(response.data);
      } else {
        setStatsError(response.error?.message || 'Failed to load webhook statistics');
      }
    } catch (err) {
      console.error('Error fetching webhook stats:', err);
      setStatsError('Failed to load webhook statistics');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fetch webhook deliveries (logs)
  const fetchDeliveries = useCallback(async () => {
    try {
      setDeliveriesLoading(true);
      setDeliveriesError(null);
      const params = new URLSearchParams({
        page: '1',
        pageSize: '20',
        ...filters
      });

      const response = await api.get<WebhookDeliveriesResponse>(`/api/admin/webhooks/deliveries?${params}`);

      if (response.success && response.data) {
        setDeliveries(response.data.deliveries || []);
        setTotalDeliveries(response.data.total || 0);
      } else {
        setDeliveriesError(response.error?.message || 'Failed to load delivery logs');
      }
    } catch (err) {
      console.error('Error fetching webhook deliveries:', err);
      setDeliveriesError('Failed to load delivery logs');
    } finally {
      setDeliveriesLoading(false);
    }
  }, [filters]);

  // Initial data fetch
  useEffect(() => {
    if (isInitialized) {
      fetchWebhooks();
      fetchStats();
      fetchDeliveries();
    }
  }, [isInitialized, fetchWebhooks, fetchStats, fetchDeliveries]);

  const handleRefreshWebhooks = useCallback(() => {
    if (page !== 1) {
      setPage(1);
    } else {
      fetchWebhooks();
    }
  }, [fetchWebhooks, page]);

  // Handle create webhook
  const handleCreateWebhook = async () => {
    try {
      setLoading(true);
      const response = await api.post<ActionResponse>('/api/admin/webhooks', newWebhook);

      if (response.success) {
        setIsCreateDialogOpen(false);
        setNewWebhook({ name: '', url: '', events: [], secret: '' });
        fetchWebhooks();
        fetchStats();
        toast.success('Webhook created successfully');
      } else {
        toast.error(response.error?.message || 'Failed to create webhook');
      }
    } catch (err) {
      console.error('Error creating webhook:', err);
      toast.error('Failed to create webhook');
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle webhook status
  const handleToggleStatus = async (webhook: WebhookConfig) => {
    try {
      const newStatus = webhook.status === 'active' ? 'inactive' : 'active';
      const response = await api.put<ActionResponse>(`/api/admin/webhooks/${webhook.id}`, { status: newStatus });

      if (response.success) {
        setWebhooks(prev =>
          prev.map(w =>
            w.id === webhook.id ? { ...w, status: newStatus } : w
          )
        );
        fetchStats();
        toast.success(`Webhook ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      } else {
        toast.error(response.error?.message || 'Failed to update webhook status');
      }
    } catch (err) {
      console.error('Error toggling webhook status:', err);
      toast.error('Failed to update webhook status');
    }
  };

  // Handle delete webhook (called after AlertDialog confirmation)
  const handleDeleteWebhook = async (webhookId: number) => {
    try {
      setLoading(true);
      const response = await api.delete<ActionResponse>(`/api/admin/webhooks/${webhookId}`);

      if (response.success) {
        fetchWebhooks();
        fetchStats();
        toast.success('Webhook deleted');
      } else {
        toast.error(response.error?.message || 'Failed to delete webhook');
      }
    } catch (err) {
      console.error('Error deleting webhook:', err);
      toast.error('Failed to delete webhook');
    } finally {
      setLoading(false);
    }
  };

  // Handle test webhook
  const handleTestWebhook = async () => {
    if (!selectedWebhook) return;

    try {
      setTestingWebhookId(selectedWebhook.id);
      const response = await api.post<TestWebhookResponse>(`/api/admin/webhooks/${selectedWebhook.id}/test`);

      if (response.success && response.data) {
        setTestResult(response.data);
      } else {
        toast.error(response.error?.message || 'Failed to test webhook');
      }
    } catch (err) {
      console.error('Error testing webhook:', err);
      toast.error('Failed to test webhook');
    } finally {
      setTestingWebhookId(null);
    }
  };

  // Handle event checkbox change
  const handleEventToggle = (eventType: string) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(eventType)
        ? prev.events.filter(e => e !== eventType)
        : [...prev.events, eventType]
    }));
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter webhooks by search term
  const filteredWebhooks = webhooks.filter(webhook => {
    const search = (filters.search as string) || '';
    const status = (filters.status as string) || '';

    const matchesSearch = search === '' ||
      webhook.name.toLowerCase().includes(search.toLowerCase()) ||
      webhook.url.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = status === '' || status === 'all' || webhook.status === status;

    return matchesSearch && matchesStatus;
  });

  // Calculate stats values
  const activeCount = stats?.activeWebhooks || 0;
  const failedCount = stats?.failedDeliveries || 0;
  const successRate = stats?.successRate || 0;

  if (loading && webhooks.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <AdminLayout>
      <PageHeader
        title="Webhooks"
        actions={(
          <Button size="sm" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Webhook
          </Button>
        )}
      />

      {statsError ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <AsyncErrorState
            layout="inline"
            title="Statistics unavailable"
            message={statsError}
            onRetry={() => void fetchStats()}
            disabled={statsLoading}
          />
        </div>
      ) : null}

      {/* Main Content Tabs */}
      <Tabs defaultValue="webhooks" className="space-y-6">
        <TabsList>
          <TabsTrigger value="webhooks">
            Webhooks ({totalWebhooks})
            {activeCount > 0 && <span className="ml-1.5 text-xs text-primary">{activeCount} active</span>}
          </TabsTrigger>
          <TabsTrigger value="deliveries">
            Delivery Logs ({totalDeliveries})
            {successRate > 0 && <span className="ml-1.5 text-xs text-muted-foreground">{successRate.toFixed(0)}% success</span>}
          </TabsTrigger>
        </TabsList>

         {/* Webhooks Tab */}
         <TabsContent value="webhooks" className="space-y-6">
          <div className="@container/page-toolbar">
            <PageToolbar
              leading={(
                <FilterBar
                  filters={[
                    {
                      id: 'search',
                      label: 'Search',
                      type: 'search',
                      placeholder: 'Search webhooks by name or URL...'
                    },
                    {
                      id: 'status',
                      label: 'Status',
                      type: 'select',
                      options: [
                        { value: 'all', label: 'All Status' },
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' }
                      ],
                      placeholder: 'All Status'
                    }
                  ]}
                  activeFilters={filters}
                  onFilterChange={setFilters}
                />
              )}
	              trailing={(
	                <Button
	                  variant="outline"
	                  size="icon"
	                  onClick={handleRefreshWebhooks}
	                  className="shrink-0"
	                  aria-label="Refresh webhooks"
	                  title="Refresh"
	                >
	                  <RefreshCw className="h-4 w-4" />
                </Button>
              )}
            />
          </div>

          {/* Webhooks Table */}
          <PageSection
            title={`Webhooks (${filteredWebhooks.length})`}
            description="Manage webhook endpoints and verify delivery health."
            className="border-border/70"
            contentClassName="p-3 sm:p-4"
          >
            {filteredWebhooks.length === 0 && !error ? (
              <div className="text-center py-10">
                <Webhook className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No webhooks found</h3>
                <p className="text-muted-foreground">
                  {Object.keys(filters).length > 0
                    ? 'Try adjusting your search or filters'
                    : 'Add your first webhook to start receiving events'
                  }
                </p>
              </div>
            ) : !error ? (
              <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>URL</TableHead>
                      <TableHead>Events</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Triggered</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[180px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredWebhooks.map((webhook) => (
                      <TableRow key={webhook.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Webhook className="h-4 w-4 text-muted-foreground" />
                            <button
                              type="button"
                              className="font-medium hover:text-primary hover:underline focus:outline-none focus:underline cursor-pointer text-left"
                              onClick={() => {
                                setSelectedWebhook(webhook);
                                setIsViewDialogOpen(true);
                              }}
                              title="View webhook details"
                            >
                              {webhook.name}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm truncate max-w-[200px] block" title={webhook.url}>
                            {webhook.url}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {webhook.events.slice(0, 2).map((event) => (
                              <Badge key={event} variant="outline" className="text-xs">
                                {event.split('.')[1] || event}
                              </Badge>
                            ))}
                            {webhook.events.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{webhook.events.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={webhook.status === 'active' ? 'default' : 'secondary'}>
                            {webhook.status === 'active' ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Active
                              </>
                            ) : (
                              <>
                                <Pause className="h-3 w-3 mr-1" />
                                Inactive
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">-</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{formatDate(webhook.createdAt)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedWebhook(webhook);
                                setIsTestDialogOpen(true);
                              }}
                              title="Test Webhook"
                            >
                              {testingWebhookId === webhook.id ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(webhook)}
                              title={webhook.status === 'active' ? 'Disable' : 'Enable'}
                              aria-label={webhook.status === 'active' ? 'Disable webhook' : 'Enable webhook'}
                            >
                              {webhook.status === 'active' ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedWebhook(webhook);
                                setIsViewDialogOpen(true);
                              }}
                              title="View Details"
                              aria-label="View webhook details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => setWebhookToDelete(webhook)}
                              title="Delete Webhook"
                              aria-label="Delete webhook"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : null}

              {/* Pagination */}
              {totalWebhooks > pageSize && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-4">
                    Page {page} of {Math.ceil(totalWebhooks / pageSize)}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.min(Math.ceil(totalWebhooks / pageSize), p + 1))}
                    disabled={page === Math.ceil(totalWebhooks / pageSize) || loading}
                  >
                    Next
                  </Button>
                </div>
              )}
          </PageSection>
        </TabsContent>

        {/* Delivery Logs Tab */}
        <TabsContent value="deliveries" className="space-y-6">
          <div className="@container/page-toolbar">
            <PageToolbar
              leading={(
                <FilterBar
                  filters={[
                    {
                      id: 'eventType',
                      label: 'Event Type',
                      type: 'select',
                      options: [
                        { value: 'all', label: 'All events' },
                        ...EVENT_TYPES.map(event => ({ value: event, label: event }))
                      ],
                      placeholder: 'All events'
                    }
                  ]}
                  activeFilters={filters}
                  onFilterChange={setFilters}
                />
              )}
              trailing={(
                <Button
                  variant="outline"
                  size="icon"
                  onClick={fetchDeliveries}
                  aria-label="Refresh delivery logs"
                  title="Refresh"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              )}
            />
          </div>

          <PageSection
            title={`Delivery Logs (${deliveries.length})`}
            description="Inspect recent delivery attempts and response status codes."
            className="border-border/70"
            contentClassName="p-3 sm:p-4"
          >
            {deliveriesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : deliveriesError ? (
                <AsyncErrorState
                  title="Could not load delivery logs"
                  message={deliveriesError}
                  onRetry={() => void fetchDeliveries()}
                  disabled={deliveriesLoading}
                  compact
                />
              ) : deliveries.length === 0 ? (
                <div className="text-center py-10">
                  <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No deliveries found</h3>
                  <p className="text-muted-foreground">
                    Webhook delivery attempts will appear here
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Triggered</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Response</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deliveries.map((delivery) => (
                      <TableRow key={delivery.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{formatDate(delivery.triggeredAt)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{delivery.eventType}</Badge>
                        </TableCell>
                        <TableCell>
                          {delivery.success ? (
                            <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {delivery.statusCode}
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              {delivery.statusCode || 'Error'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">{delivery.duration}ms</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
                            {delivery.success
                              ? 'OK'
                              : delivery.errorMessage || 'Request failed'
                            }
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
          </PageSection>
        </TabsContent>
      </Tabs>

      {/* Create Webhook Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Webhook</DialogTitle>
            <DialogDescription>
              Configure a new webhook endpoint to receive system events.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="My Webhook"
                value={newWebhook.name}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="url">Endpoint URL</Label>
              <Input
                id="url"
                placeholder="https://example.com/webhook"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Events to Subscribe</Label>
              <div className="grid grid-cols-2 gap-2">
                {EVENT_TYPES.map(event => (
                  <label key={event} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={newWebhook.events.includes(event)}
                      onChange={() => handleEventToggle(event)}
                      className="rounded"
                    />
                    {event}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="secret">Secret (optional)</Label>
              <Input
                id="secret"
                type="password"
                placeholder="Webhook secret for signature verification"
                value={newWebhook.secret || ''}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, secret: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateWebhook}
              disabled={!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0 || loading}
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Create Webhook'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Webhook Dialog */}
      <Dialog open={isTestDialogOpen} onOpenChange={(open) => {
        setIsTestDialogOpen(open);
        if (!open) {
          setTestResult(null);
          setSelectedWebhook(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Webhook</DialogTitle>
            <DialogDescription>
              Send a test event to verify your webhook endpoint is working.
            </DialogDescription>
          </DialogHeader>
          {selectedWebhook && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedWebhook.name}</p>
                <p className="text-sm text-muted-foreground font-mono">{selectedWebhook.url}</p>
              </div>
              {testResult ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    {testResult.success ? (
                      <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Success ({testResult.statusCode})
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-4 w-4 mr-1" />
                        Failed
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                      {testResult.duration}ms
                    </span>
                  </div>
                  <div>
                    <Label>Response Body</Label>
                    <pre className="bg-muted p-4 rounded text-sm overflow-x-auto max-h-40">
                      {testResult.response || 'No response body'}
                    </pre>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Click "Send Test" to trigger a test event.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTestDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleTestWebhook}
              disabled={testingWebhookId !== null}
            >
              {testingWebhookId !== null ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Testing...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Webhook Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={(open) => {
        setIsViewDialogOpen(open);
        if (!open) setSelectedWebhook(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Webhook Details</DialogTitle>
            <DialogDescription>
              View and manage webhook configuration.
            </DialogDescription>
          </DialogHeader>
          {selectedWebhook && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <p className="font-medium">{selectedWebhook.name}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">
                    <Badge variant={selectedWebhook.status === 'active' ? 'default' : 'secondary'}>
                      {selectedWebhook.status}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-2">
                  <Label>URL</Label>
                  <p className="font-mono text-sm bg-muted p-2 rounded">{selectedWebhook.url}</p>
                </div>
                <div className="col-span-2">
                  <Label>Subscribed Events</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedWebhook.events.map(event => (
                      <Badge key={event} variant="outline">{event}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Created</Label>
                  <p className="text-sm">{formatDate(selectedWebhook.createdAt)}</p>
                </div>
                <div>
                  <Label>Last Updated</Label>
                  <p className="text-sm">{formatDate(selectedWebhook.updatedAt)}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error State */}
      {error && (
        <Card className="border-destructive/30">
          <CardContent className="p-4">
            <AsyncErrorState
              layout="inline"
              title="Webhooks failed to load"
              message={error}
              onRetry={() => void fetchWebhooks()}
              disabled={loading}
            />
          </CardContent>
        </Card>
      )}
      </AdminLayout>

    {/* Delete Confirmation AlertDialog — rendered outside AdminLayout so it overlays correctly */}
    <AlertDialog open={!!webhookToDelete} onOpenChange={(open) => !open && setWebhookToDelete(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete webhook?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>{webhookToDelete?.url}</strong>?
            This will stop all future deliveries to this endpoint and cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => {
              if (webhookToDelete) {
                handleDeleteWebhook(webhookToDelete.id);
                setWebhookToDelete(null);
              }
            }}
          >
            Delete webhook
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
